/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-report-template-grid.data.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { TFunction } from 'i18next';
import { OpraReportTemplateFields } from '@components/admin/admin-report-template-grid/admin-report-template-grid.interface';
import { useGridCells } from 'hooks/index';
import { IExpandableGridColumnV2 } from 'views/components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { GridColumnDateFilter, GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';

const { DateCell, MaxLengthCell, NumberCell, YesNoCell } = useGridCells();

export const RIGHT_END_REPORT_TEMPLATE_COLUMNS = (
    t: TFunction,
): IExpandableGridColumnV2<keyof OpraReportTemplateFields>[] => [
    {
        label: t('admin:powerBITemplate'),
        field: '_powerBITemplateName',
        id: 'template-power-bi',
        headerClass: 'border',
        minWidth: 208,
        cells: { data: MaxLengthCell(50) },
    },
    {
        label: t('admin:inclSignatures'),
        id: 'inclSignatures',
        field: 'includeSignatures',
        minWidth: 140,
        cells: { data: YesNoCell },
        filterUi: GridColumnMenuActiveFilter,
    },
    {
        label: t('admin:inclNameAndVersion'),
        id: 'inclNameAndVersion',
        field: 'includeNameAndVersion',
        minWidth: 145,
        cells: { data: YesNoCell },
        filterUi: GridColumnMenuActiveFilter,
    },
    {
        label: t('admin:inclReviews'),
        id: 'inclReviews',
        field: 'includeReviews',
        minWidth: 123,
        cells: { data: YesNoCell },
        filterUi: GridColumnMenuActiveFilter,
    },
    {
        label: t('admin:inclNotApplicableRisks'),
        id: 'inclNotApplicableRisks',
        field: 'includeNotApplicableRisks',
        minWidth: 172,
        cells: { data: YesNoCell },
        filterUi: GridColumnMenuActiveFilter,
    },
    {
        label: t('admin:inclInternalRisks'),
        id: 'inclInternalRisks',
        field: 'includeInternalRisks',
        minWidth: 148,
        cells: { data: YesNoCell },
        filterUi: GridColumnMenuActiveFilter,
    },
    {
        label: t('admin:inclComparisonDataset'),
        id: 'inclComparisonDataset',
        field: 'includeComparisonDataset',
        minWidth: 175,
        cells: { data: YesNoCell },
        filterUi: GridColumnMenuActiveFilter,
    },
    {
        label: t('ra:createdBy'),
        field: '_createdByString',
        headerClass: 'border',
        id: 'template-createdby',
        showByDefault: false,
        minWidth: 140,
    },
    {
        label: t('ra:createdDate'),
        field: 'createdDate',
        headerClass: 'border',
        filter: 'date',
        id: 'template-created',
        showByDefault: false,
        minWidth: 140,
        cells: { data: DateCell },
        filterUi: GridColumnDateFilter,
    },
    {
        label: t('ra:updatedBy'),
        field: '_updatedByString',
        headerClass: 'border',
        id: 'template-updatedby',
        showByDefault: false,
        minWidth: 145,
    },
    {
        label: t('ra:updatedDate'),
        field: 'updatedDate',
        headerClass: 'border',
        filter: 'date',
        id: 'template-updated',
        showByDefault: false,
        minWidth: 160,
        cells: { data: DateCell },
        filterUi: GridColumnDateFilter,
    },
];

export const REPORT_TEMPLATE_COLUMNS = (t: TFunction): IExpandableGridColumnV2<keyof OpraReportTemplateFields>[] => [
    {
        label: t('admin:templateName'),
        field: 'fullName',
        id: 'template-name',
        headerClass: 'border',
        minWidth: 208,
        lockedByDefault: true,
        cells: { data: MaxLengthCell(50) },
    },
    {
        label: t('ra:description'),
        field: 'description',
        id: 'template-description',
        headerClass: 'border',
        minWidth: 208,
        cells: { data: MaxLengthCell(50) },
    },
    {
        label: t('common:module'),
        field: 'moduleName',
        id: 'template-module',
        headerClass: 'border',
        minWidth: 120,
        cells: { data: MaxLengthCell(50) },
    },
    {
        label: t('common:format'),
        field: 'reportFormatName',
        id: 'template-format',
        headerClass: 'border',
        minWidth: 120,
        cells: { data: NumberCell },
    },
    {
        label: t('ra:status'),
        field: 'reportStatusName',
        id: 'template-status',
        headerClass: 'border',
        minWidth: 150,
    },
    {
        label: t('admin:retirementDate'),
        field: 'retirementDate',
        headerClass: 'border',
        filter: 'date',
        id: 'template-retirement-date',
        lockedByDefault: true,
        minWidth: 175,
        cells: { data: (params: any) => DateCell({ ...params, dateFormat: 'DD-MMM-YYYY hh:mm A UTC' }) },
        filterUi: GridColumnDateFilter,
    },
    ...RIGHT_END_REPORT_TEMPLATE_COLUMNS(t),
    {
        label: t('id'),
        field: '_itemId',
        minWidth: 150,
        id: 'template-id',
        showByDefault: false,
    },
];
